import React, { useState, useEffect } from "react";
import { Pie } from "react-chartjs-2";
import PercentIcon from "@mui/icons-material/Percent";
import Slider from "@mui/material/Slider";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import { RootState } from "../Redux/store";
import PDFGenerator from "../pdf/pdf-fixed-deposit";
import ReactGA from "react-ga4";
import SaveTitleDialog from "../dialog-box/SaveTitleDialog";
import { FixedDeposit } from "../models/fixed-deposit";
import { saveFD, updateFD } from "../Database/fixed-deposit";
import { IconButton } from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCurrencySymbol } from "../Redux/currencySlice";
import BootstrapTooltip from "../components/tooltip";
import SavedSnackbar from "../utils/saved-snackbar";

const FixedDepositCalculator: React.FC<{
  fixedDeposit?: FixedDeposit;
}> = ({}) => {
  const [fixedData, setFixedDepositData] = useState<FixedDeposit | null>(null);
  const location = useLocation();

  const currencySymbol = useSelector(
    (state: RootState) => state.country.currencySymbol
  );
  const [showResult, setShowResult] = useState(false);
  const [principal, setPrincipal] = useState<string>("10,00,000");
  const [rate, setRate] = useState<string>("6");
  const [period, setPeriod] = useState<number | null>(5);
  const [totalAmount, setTotalAmount] = useState<number | null>(null);
  const [totalInterest, setTotalInterest] = useState<number | null>(null);
  const [periodUnit, setPeriodUnit] = useState<"years" | "months">("years");
  const MAX_PERIOD = 10;
  const MAX_PRINCIPAL = 10000000;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [icon, setIcon] = useState<React.ReactNode>(<FavoriteBorderIcon />);
  const dispatch = useDispatch();
  const [, setSymbol] = useState(currencySymbol);
  const [principalForChart, setPrincipalForChart] = useState(0);
  const [interestForChart, setInterestForChart] = useState(0);
  // const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [errors, setErrors] = useState({
    principal: "",
    rate: "",
    period: "",
  });
  const [showPdfButton, setShowPdfButton] = useState(false);
  const [savedTitle, setSavedTitle] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isCalculated, setIsCalculated] = useState(false);
 

  useEffect(() => {
    const { fixed: passedFixed, currencySymbol: passedCurrencySymbol } =
      location.state || {};

    console.log("Passed fixed deposit data:", passedFixed);

    if (passedCurrencySymbol) {
      dispatch(setCurrencySymbol(passedCurrencySymbol));
      setSymbol(passedCurrencySymbol);
    } else {
      setSymbol(currencySymbol);
    }

    if (passedFixed) {
      setFixedDepositData(passedFixed);
      setIcon(<FavoriteIcon style={{ color: "red" }} />);
      setPrincipal(passedFixed.principal.toString());
      setRate(passedFixed.rate.toString());
      setPeriod(passedFixed.period.toString());
      setTitle(passedFixed.title);
      setPeriodUnit(passedFixed.timeType);
      setSavedTitle(passedFixed.title || "");
      handleCalculate();
    }
    handleCalculate();
  }, [location.state, fixedData]);

  const handleReset = () => {
    ReactGA.event({
      category: "Fixed Deposit calculator",
      action: "FD Reset Button",
      label: "FD Calculator",
    });
    setPrincipal("10,00,000");
    setRate("6");
    setPeriod(5);
    setTotalAmount(null);
    setTotalInterest(null);
    setShowPdfButton(false);
    setShowResult(true);

    setErrors({
      principal: "",
      rate: "",
      period: "",
    });
  };

  const handleSubmit = async () => {
    if (!validateInputs()) {
      return;
    }

    const fixedDeposit: FixedDeposit = {
      title: title,
      principal: parseFloat(principal.replace(/,/g, "")),
      rate: parseFloat(rate),
      period: parseInt(period?.toString() || "0", 10),
      currencySymbol,
      timeType: periodUnit,
    };
    console.log("Fixed deposit object:", fixedDeposit);

    if (fixedData?.id) {
      await updateFD(fixedData.id, fixedDeposit);
      console.log(`fixedData with ID ${fixedData.id} updated successfully.`);
    } else {
      await saveFD(fixedDeposit);
      console.log("New fixedData created successfully.");
    }
    setSnackbarMessage("Saved successfully!");
    setSnackbarOpen(true);
    setIcon(<FavoriteIcon style={{ color: "red" }} />);
    handleDialogClose();
    setTitle("");
  };
  const validateInputs = () => {
    const newErrors = {
      principal: "",
      rate: "",
      period: "",
    };
    let isValid = true;

    const minprincipal = 5000;
    const maxPrincipal = MAX_PRINCIPAL; 
    const minRate = 1;
    const maxRate = 30;


    if (!principal) {
      newErrors.principal = "Principal amount is mandatory.";
      isValid = false;
    } else {
      const principalValue = parseFloat(principal.replace(/,/g, ""));
      if (principalValue < minprincipal) {
        newErrors.principal = `Principal amount can't be less than ${currencySymbol} ${minprincipal}.`;
        isValid = false;
      } else if (principalValue > maxPrincipal) {
        newErrors.principal = `Principal amount can't exceed ${currencySymbol} ${maxPrincipal}.`;
        isValid = false;
      }
    }
    
  const rateValue = parseFloat(rate.replace(/,/g, ""));
  if (!rate) {
    newErrors.rate = "Interest rate is mandatory.";
    isValid = false;
  } else if (isNaN(rateValue) || rateValue < minRate || rateValue > maxRate) {
    newErrors.rate = "Interest rate must be between 1 and 15.";
    isValid = false;
  }


    if (periodUnit === "months" && (!period || period < 1 || period > 120)) {
      newErrors.period = "Period must be between 1 and 120 months.";
      isValid = false;
    } else if (
      periodUnit === "years" &&
      (!period || period < 1 || period > MAX_PERIOD)
    ) {
      newErrors.period = "Period must be between 1 and 10 years.";
      isValid = false;
    }

    if (!period) {
      newErrors.period = "Period unit (years or months) is mandatory.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleCalculate = () => {
    ReactGA.event({
      category: "Fixed Deposit calculator",
      action: "Fixed Deposit Calculate Button",
      label: "Fixed Deposit Calculator",
    });
  
    if (!validateInputs()) {
      return;
    }
  
    const periodValue = period;
  
    const principalValue = parseFloat(principal.replace(/,/g, ""));
    const rateValue = parseFloat(rate) / 100;
    const safePeriodValue = periodValue || 0;

    const totalYears =
      periodUnit === "years" ? safePeriodValue : safePeriodValue / 12;
    const n = periodUnit === "years" ? 1 : 12; 

    const totalAmountValue =
      principalValue * Math.pow(1 + rateValue / n, n * totalYears);
    const interestEarned = totalAmountValue - principalValue;
    console.log("### in screen rate :" + (rateValue / n) )
  
    setTotalAmount(totalAmountValue);
    setTotalInterest(interestEarned);
    setPrincipalForChart(principalValue);
    setInterestForChart(interestEarned);
    setShowPdfButton(true);
    setIsCalculated(true);
  };
  

  const formatNumber = (num: number | null) => {
    return num === null
      ? ""
      : `${new Intl.NumberFormat("en-IN", {
          // minimumFractionDigits: 2,
          // maximumFractionDigits: 2,
        }).format(num)}`;
  };

  const formatPrincipal = (value: string) => {
    const numberValue = parseFloat(value.replace(/,/g, "").replace(/\s+/g, ""));
    if (value.length > 0 && value[0] === "0" && value !== "0") {
      return;
    }
    if (value === "0") {
      return "";
    }
    if (!isNaN(numberValue) && numberValue <= 10000000) {
      const formattedValue = new Intl.NumberFormat("en-IN").format(numberValue);
      setPrincipal(formattedValue);
    } else if (value === "") {
      setPrincipal("");
    }
  };

  const handleNumberInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<string>>,
    maxLimit: number,
    decimalLimit: number = 2
  ) => {
    const value = e.target.value;
    const validValue = value.replace(/[^0-9.]/g, "");

    if (value.length > 0 && value[0] === "0" && value !== "0") {
      return;
    }
    if (value === "0") {
      return "";
    }

    const parts = validValue.split(".");
    if (parts.length > 1 && parts[1].length > decimalLimit) {
      parts[1] = parts[1].slice(0, decimalLimit);
    }

    const formattedValue = parts.join(".");

    if (parseFloat(formattedValue) <= maxLimit || formattedValue === "") {
      setter(formattedValue);
    }
  };
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPrincipal(value);
  };
  const handleDialogOpen = () => {
    setDialogOpen(true);
  };
  const handleDialogClose = () => {
    setDialogOpen(false);
    setTitle("");
  };

  const handleCancel = () => {
    handleDialogClose();
  };
  const total = principalForChart + interestForChart
  const percentagePrincipal = Math.round((principalForChart/total)*100)
  const percentageInterest = Math.round((interestForChart/total)*100)

  const chartData = {
    labels: ["Principal Amount %", "Total Interest Earned %"],
    datasets: [
      {
        data: [percentagePrincipal, percentageInterest],
        backgroundColor: ["#4CAF50", "#FF9800"],
        hoverBackgroundColor: ["#45a049", "#ff8c00"],
      },
    ],
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCalculate();
    }
  };

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason: "timeout" | "clickaway" | "escapeKeyDown"
  ) => {
    if (reason === 'clickaway') {
      return; 
    }
    setSnackbarOpen(false); 
  };

  return (
    <>
      <div className="container mt-5" onKeyPress={handleKeyPress}>
        <Helmet>
          <title>Fixed Deposit Calculator </title>
          <meta
            name="description"
            content="Use our Fixed Deposit (FD) Calculator to estimate the maturity amount and interest earned on your investment. Get accurate results and plan your savings efficiently."
          />
          <link rel="canonical" href="/fixed-deposit-calculator.html" />
        </Helmet>
        <SavedSnackbar
        open={snackbarOpen} 
        message={snackbarMessage} 
        onClose={handleSnackbarClose} 
      />
        <div className="container mt-5">
          <div className="d-flex justify-content-between align-items-center">
            <h5
              className="mb-2"
              style={{
                fontFamily: "serif",
                fontSize: "30px",
                fontWeight: "bold",
              }}
            >
              FD Calculator
            </h5>
          </div>
          <p>
            The Fixed Deposit (FD) Calculator helps you estimate the maturity
            amount and interest earned on your fixed deposit based on the
            deposit amount, interest rate, and tenure. With this tool, you can
            calculate the returns on your FD, factoring in annual compound
            interest, making it an essential resource for investment planning
            and understanding the growth of your deposit over time.
          </p>
        </div>
        <div className="row justify-content-center">
          {savedTitle && (
            <Typography variant="h6" style={{ marginLeft: "20px" }}>
              {savedTitle}
            </Typography>
          )}
          <div className="col-12 col-md-6 mb-4">
            <div
              className="card shadow-sm"
              style={{
                width: "100%",
                height: "560px",
                margin: "0 auto",
                overflow: "auto",
              }}
            >
              <div className="card-body">
                <div className="mb-4">
                  <label htmlFor="principal" className="form-label">
                    Principal Amount ({currencySymbol})
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className={`form-control ${
                        errors.principal ? "is-invalid" : ""
                      }`}
                      id="principal"
                      value={principal}
                      onChange={(e) => formatPrincipal(e.target.value)}
                      onBlur={handleCalculate}
                      aria-label="Principal Amount"
                    />
                  </div>
                  {errors.principal && (
                    <div className="text-danger">{errors.principal}</div>
                  )}
                </div>
                <div className="mb-3 d-flex justify-content-center">
                  <Slider
                    min={5000}
                    max={MAX_PRINCIPAL}
                    step={1000}
                    value={parseFloat(principal.replace(/,/g, ""))}
                    onChange={(e, newValue) =>
                      setPrincipal(newValue.toString())
                    }
                    valueLabelDisplay="auto"
                    shiftStep={30}
                    sx={{ width: "95%" }}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="rate" className="form-label">
                    Annual Interest Rate (%)
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className={`form-control ${
                        errors.rate ? "is-invalid" : ""
                      }`}
                      id="rate"
                      value={rate}
                      onChange={(e) => handleNumberInput(e, setRate, 15, 2)}
                    />
                    <span className="input-group-text">
                      <PercentIcon />
                    </span>
                  </div>
                  {errors.rate && (
                    <div className="text-danger">{errors.rate}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="period" className="form-label">
                    Period
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className={`form-control ${
                        errors.period ? "is-invalid" : ""
                      }`}
                      id="period"
                      value={period !== null ? period : ""}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, "");
                        if (value === "") {
                          setPeriod(null);
                          setErrors((prev) => ({ ...prev, period: "" })); 
                        } else {
                          const numericValue = parseFloat(value);
                          if (
                            !isNaN(numericValue) &&
                            ((periodUnit === "years" &&
                              numericValue >= 1 &&
                              numericValue <= 10) ||
                              (periodUnit === "months" &&
                                numericValue >= 1 &&
                                numericValue <= 120))
                          ) {
                            setPeriod(numericValue);
                            setErrors((prev) => ({ ...prev, period: "" })); 
                          } else {
                            if (periodUnit === "years") {
                              if (numericValue > 10) {
                                setErrors((prev) => ({
                                  ...prev,
                                  period: "Maximum period is 10 years.",
                                }));
                              }
                            } else if (periodUnit === "months") {
                              if (numericValue > 120) {
                                setErrors((prev) => ({
                                  ...prev,
                                  period: "Maximum period is 120 months.",
                                }));
                              }
                            }
                          }
                        }
                      }}
                    />

                    <div className="input-group-append">
                      <div
                        className="btn-group"
                        role="group"
                        aria-label="Period Unit"
                      >
                        {/* Years button */}
                        <input
                          type="radio"
                          className="btn-check"
                          name="periodUnit"
                          id="years"
                          value="years"
                          checked={periodUnit === "years"}
                          onChange={() => {
                            if (periodUnit === "months") {
                              setPeriod((prevPeriod) => prevPeriod || 0 / 12);
                            }
                            setPeriodUnit("years");
                          }}
                        />
                        <label
                          className="btn btn-outline-primary rounded-0"
                          htmlFor="years"
                        >
                          Years
                        </label>

                        {/* Months button */}
                        <input
                          type="radio"
                          className="btn-check"
                          name="periodUnit"
                          id="months"
                          value="months"
                          checked={periodUnit === "months"}
                          onChange={() => {
                            if (periodUnit === "years") {
                              setPeriod((prevPeriod) => prevPeriod || 0 * 12);
                            }
                            setPeriodUnit("months");
                          }}
                        />
                        <label
                          className="btn btn-outline-primary"
                          htmlFor="months"
                        >
                          Months
                        </label>
                      </div>
                    </div>
                  </div>
                  {errors.period && (
                    <div className="text-danger">{errors.period}</div>
                  )}
                </div>

                <div
                  className="d-flex justify-content-center "
                  style={{ marginTop: "60px" }}
                >
                  <button
                    className="btn btn-primary btn-lg "
                    onClick={handleCalculate}
                  >
                    Calculate
                  </button>
                </div>
                <div className="d-flex justify-content-center">
                  <a
                    onClick={handleReset}
                    style={{ cursor: "pointer", textDecoration: "none" }}
                    className=" pt-2"
                  >
                    Reset
                  </a>
                </div>
              </div>
            </div>
          </div>

          {totalInterest !== null && totalAmount !== null && (
            <div className="col-12 col-md-6 mb-4">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-4">Result</h5>
                    <BootstrapTooltip title="Save Calculation" placement="top">
                      <IconButton color="secondary" onClick={handleDialogOpen}>
                        {icon}
                      </IconButton>
                    </BootstrapTooltip>
                  </div>
                  <p className="card-text">
                    <strong>Prncipal Amount:</strong>
                    <span className="float-end">
                      {" "}
                      {currencySymbol} {formatNumber(Math.ceil(principalForChart))}
                    </span>
                  </p>
                  <p className="card-text">
                    <strong>Total Interest Earned:</strong>
                    <span className="float-end">
                      {" "}
                      {currencySymbol} {formatNumber(Math.ceil(totalInterest))}
                    </span>
                  </p>
                  <p className="card-text">
                    <strong>Total Amount:</strong>
                    <span className="float-end">
                      {" "}
                      {currencySymbol} {formatNumber(Math.ceil(totalAmount))}
                    </span>
                  </p>
                </div>
              </div>
              {totalAmount !== null && totalInterest !== null && (
                <div className="card shadow-sm mt-4">
                  <div className="card-body">
                    <h5 className="card-title mb-4">Distribution</h5>
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ width: "100%", height: "270px" }}
                    >
                      <Pie data={chartData} />
                    </div>
                  </div>
                </div>
              )}
              <div
                className="d-flex justify-content-end"
                style={{ marginTop: "70px", paddingLeft: "90px", marginBottom:'-20px' }}
              >
                {showPdfButton && (
                  <PDFGenerator
                    principal={parseFloat(principal.replace(/,/g, ""))}
                    totalAmount={totalAmount}
                    totalInterest={totalInterest}
                    period={period}
                    periodUnit={periodUnit}
                    rate={rate}
                    isCalculated={isCalculated} 
                  />
                )}
              </div>
            </div>
          )}
        </div>
        <SaveTitleDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          title={title}
          setTitle={setTitle}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>
    </>
  );
};

export default FixedDepositCalculator;
